import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatDateDisplay } from "../utils/date";
import { useTranslation } from "react-i18next";
import { useLocalize } from "../i18n";

import {
  toggleTileVisibility,
  setAllTilesVisibility,
  reorderTiles,
  deleteTile,
  clearAllMapState,
} from "../reducers/MapSlice";

// Sortable tile item component
function SortableTileItem({
  tile,
  index,
  isVisible,
  onToggleVisibility,
  onRemoveTile,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.tileId.toString() });
  const { t } = useTranslation();
  const localize = useLocalize();
  const indexInfo = useSelector((state) =>
    state.indices.find((i) => i.id === tile.tileParams?.index_id),
  );
  const tooltip = indexInfo ? (
    <>
      {localize(indexInfo, "full_name")}
      <br />
      {formatDateDisplay(tile.tileParams.image_date)}
    </>
  ) : (
    ""
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 1,
        px: 1,
        borderRadius: "8px",
        transition: "all 0.2s ease",
        backgroundColor: isDragging
          ? "rgba(var(--primary-rgb), 0.1)"
          : "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.03)",
        },
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      {/* Drag handle */}
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{
          color: "text.disabled",
          p: 0.5,
          cursor: "grab",
          // enables dragging on touch devices.
          touchAction: "none",
        }}
      >
        <DragHandleIcon fontSize="small" />
      </IconButton>

      <Tooltip title={tooltip} placement="left">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: isVisible ? "text.primary" : "text.disabled",
              textDecoration: isVisible ? "none" : "line-through",
            }}
          >
            {tile.tileName || t("tiles.tileN", { n: index + 1 })}
          </Typography>
        </Box>
      </Tooltip>

      <Tooltip title={isVisible ? t("tiles.hide") : t("tiles.show")}>
        <IconButton
          size="small"
          aria-label={isVisible ? t("tiles.hide") : t("tiles.show")}
          onClick={() => onToggleVisibility(tile.tileId)}
          sx={{
            color: isVisible ? "primary.main" : "text.disabled",
            p: 0.5,
          }}
        >
          {isVisible ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title={t("tiles.remove")}>
        <IconButton
          size="small"
          aria-label={t("tiles.remove")}
          onClick={() => onRemoveTile(tile.tileId)}
          sx={{
            color: "error.main",
            p: 0.5,
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function TilesPanel({ tiles }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [items, setItems] = useState(tiles);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  useEffect(() => {
    setItems(tiles);
  }, [tiles]);

  const hasVisible = items.some((a) => a.visible !== false);
  const hasHidden = items.some((a) => a.visible === false);

  const [isAnimating, setIsAnimating] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(500);
  const intervalRef = useRef(null);

  const speedOptions = [
    { value: 1000, label: t("tiles.speed.slow") },
    { value: 500, label: t("tiles.speed.normal") },
    { value: 200, label: t("tiles.speed.fast") },
  ];

  const sensors = useSensors(useSensor(PointerSensor));
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);

  const handleReorderTiles = (oldIndex, newIndex) => {
    dispatch(reorderTiles({ oldIndex, newIndex }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    // `over` is null when the tile is dropped outside the list.
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(
        (item) => item.tileId.toString() === active.id,
      );
      const newIndex = items.findIndex(
        (item) => item.tileId.toString() === over.id,
      );
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);
        handleReorderTiles(oldIndex, newIndex);
      }
    }
  };

  const handleRemoveSingleTile = (tileId) => {
    if (!tileId) return;
    dispatch(deleteTile([tileId.toString()]));
    const savedVisibility = localStorage.getItem(
      `tileVisibilityState_${currentWorkspace}`,
    );
    if (savedVisibility) {
      const visibilityMap = JSON.parse(savedVisibility);
      delete visibilityMap[tileId];
      localStorage.setItem(
        `tileVisibilityState_${currentWorkspace}`,
        JSON.stringify(visibilityMap),
      );
    }
  };

  const handleDeleteAll = () => {
    setConfirmDeleteAll(false);
    setIsAnimating(false);
    setCurrentIndex(0);
    dispatch(clearAllMapState());
    localStorage.removeItem(`tileVisibilityState_${currentWorkspace}`);
  };

  const handleShowAll = () => {
    dispatch(setAllTilesVisibility({ visible: true }));
    const visibilityMap = {};
    tiles.forEach((tile) => {
      visibilityMap[tile.tileId] = true;
    });
    localStorage.setItem(
      `tileVisibilityState_${currentWorkspace}`,
      JSON.stringify(visibilityMap),
    );
  };

  const handleHideAll = useCallback(() => {
    dispatch(setAllTilesVisibility({ visible: false }));
    const visibilityMap = {};
    tiles.forEach((tile) => {
      visibilityMap[tile.tileId] = false;
    });
    localStorage.setItem(
      `tileVisibilityState_${currentWorkspace}`,
      JSON.stringify(visibilityMap),
    );
  }, [dispatch, tiles, currentWorkspace]);

  const handleShowTile = useCallback(
    (tileId) => {
      dispatch(toggleTileVisibility({ tileId, forceValue: true }));
      const savedVisibility = localStorage.getItem(
        `tileVisibilityState_${currentWorkspace}`,
      );
      const visibilityMap = savedVisibility ? JSON.parse(savedVisibility) : {};
      visibilityMap[tileId] = true;
      localStorage.setItem(
        `tileVisibilityState_${currentWorkspace}`,
        JSON.stringify(visibilityMap),
      );
    },
    [dispatch, currentWorkspace],
  );

  const handlePlay = () => {
    if (items.length === 0) return;
    if (currentIndex === 0) handleHideAll();
    setIsAnimating(true);
  };

  const handlePause = () => setIsAnimating(false);

  const currentFrame =
    currentIndex > 0 ? items[items.length - currentIndex] : null;

  const handleReset = () => {
    setIsAnimating(false);
    setCurrentIndex(0);
    handleHideAll();
  };

  useEffect(() => {
    if (isAnimating && currentIndex < items.length) {
      intervalRef.current = setInterval(() => {
        const reverseIndex = items.length - 1 - currentIndex;
        const tile = items[reverseIndex];
        if (tile && !tile.visible) {
          handleShowTile(tile.tileId);
        }
        setCurrentIndex((prev) => prev + 1);
      }, animationSpeed);
    } else if (currentIndex >= items.length) {
      setIsAnimating(false);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [
    isAnimating,
    currentIndex,
    items,
    animationSpeed,
    handleShowTile,
    handleHideAll,
  ]);

  const handleToggleVisibility = (tileId) => {
    dispatch(toggleTileVisibility({ tileId }));
    const savedVisibility = localStorage.getItem(
      `tileVisibilityState_${currentWorkspace}`,
    );
    const visibilityMap = savedVisibility ? JSON.parse(savedVisibility) : {};
    const tile = tiles.find((a) => a.tileId === tileId);
    if (tile) {
      visibilityMap[tileId] = !(tile.visible !== false);
      localStorage.setItem(
        `tileVisibilityState_${currentWorkspace}`,
        JSON.stringify(visibilityMap),
      );
    }
  };

  const visibleCount = items.filter((a) => a.visible !== false).length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 0,
        height: "auto",
        overflowY: "visible",
        overflowX: "hidden",
      }}
    >
      {/* Bulk actions header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: "primary.dark",
            textTransform: "uppercase",
          }}
        >
          {t("tiles.header", {
            count: items.length,
            max: tilesLimits.workspace_tiles,
          })}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title={t("tiles.showAll")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tiles.showAll")}
                onClick={handleShowAll}
                disabled={!hasHidden}
                sx={{ p: 0.5, color: "primary.main" }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("tiles.hideAll")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tiles.hideAll")}
                onClick={handleHideAll}
                disabled={!hasVisible}
                sx={{ p: 0.5, color: "text.secondary" }}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("tiles.removeAll")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tiles.removeAll")}
                onClick={() => setConfirmDeleteAll(true)}
                disabled={items.length === 0}
                sx={{ p: 0.5, color: "error.main" }}
              >
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Animation Controls */}
      {items.length > 0 && (
        <Box
          sx={{
            mx: 2,
            p: 1.5,
            borderRadius: "12px",
            backgroundColor: "rgba(var(--primary-rgb), 0.05)",
            border: "1px solid rgba(var(--primary-rgb), 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                textTransform: "uppercase",
              }}
            >
              {t("tiles.animation")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: isAnimating ? "success.main" : "text.secondary" }}
            >
              {visibleCount}/{items.length}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={handlePlay}
              aria-label={t("tiles.play")}
              disabled={isAnimating || currentIndex >= items.length}
              sx={{ color: "primary.main" }}
            >
              <PlayArrowIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handlePause}
              aria-label={t("tiles.pause")}
              disabled={!isAnimating}
              sx={{ color: "warning.main" }}
            >
              <PauseIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleReset}
              aria-label={t("tiles.stop")}
              sx={{ color: "error.main" }}
            >
              <StopIcon />
            </IconButton>
            <FormControl size="small" sx={{ ml: "auto", minWidth: 90 }}>
              <Select
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(e.target.value)}
                disabled={isAnimating}
                sx={{ fontSize: "11px", height: "28px" }}
              >
                {speedOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {currentFrame && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "text.secondary" }}
            >
              {t("tiles.nowShowing")}{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {currentFrame.tileName}
              </Box>
            </Typography>
          )}
        </Box>
      )}

      {/* Tile Items List */}
      <Box sx={{ px: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((t) => t.tileId.toString())}
            strategy={verticalListSortingStrategy}
          >
            {items.map((tile, idx) => (
              <SortableTileItem
                key={tile.tileId.toString()}
                tile={tile}
                index={idx}
                isVisible={tile.visible !== false}
                onToggleVisibility={handleToggleVisibility}
                onRemoveTile={handleRemoveSingleTile}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      <Dialog
        open={confirmDeleteAll}
        onClose={() => setConfirmDeleteAll(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("tiles.removeAllTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("tiles.removeAllBody", {
              count: items.length,
              workspace: currentWorkspace,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAll(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteAll} variant="contained" color="error">
            {t("tiles.removeAllButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
